import { useCallback, useEffect, useState } from 'react'
import { fetchThemePosts } from '../../api'
import { colors } from '../../colors'
import {
  BackButton,
  DateFormatter,
  InfinitePostList,
  LoginModal,
  PageContainer,
  PageHeader,
} from '../../components'
import type { BargainUserInfo, PaginationParams, Post, Theme } from '../../types'

interface ThemePostsProps {
  bargain_user_info: BargainUserInfo
  theme: Theme
  posts: Post[]
  random_seed: number
}

const styles: Record<string, React.CSSProperties> = {
  filterButtons: {
    display: 'flex',
    gap: '8px',
    padding: '8px 14px',
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFFFFF',
    zIndex: 10,
  },
  filterButton: {
    width: 'auto',
    height: '40px',
    borderRadius: '20px',
    border: '1px solid #00000018',
    padding: '7px 14px',
    gap: '4px',
    background: '#FFFFFF',
    color: '#000000DE',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    opacity: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterButtonActive: {
    background: colors['keijiban-coral'],
    border: 'none',
    color: 'white',
  },
  themeInfo: {
    backgroundColor: colors['keijiban-coral-bg'],
    padding: '12px',
    marginBottom: '8px',
  },
  themeDate: {
    fontSize: '14px',
    color: colors['keijiban-brown'],
    textAlign: 'center',
    fontWeight: '300',
  },
  themeTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: colors['keijiban-brown'],
    textAlign: 'center',
    marginBottom: '0',
    lineHeight: '1.4',
  },
  themeDescription: {
    fontSize: '14px',
    fontWeight: '600',
    color: colors['keijiban-brown'],
    textAlign: 'center',
    marginBottom: '0',
    whiteSpace: 'pre-wrap',
  },
  caution: {
    fontSize: '10px',
    color: colors['text-secondary'],
    backgroundColor: '#EDF1F2',
    padding: '8px',
    margin: '0 14px 8px',
    borderRadius: '4px',
  },
  postList: {
    paddingBottom: '100px', // フッターの高さ分の余白を確保
  },
  footerButtons: {
    boxSizing: 'border-box',
    maxWidth: '640px',
    width: '100%',
    position: 'fixed',
    bottom: '16px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '8px 14px',
    gap: '8px',
  },
  filterLoadingOverlay: {
    position: 'relative',
    minHeight: '200px',
  },
  filterLoadingSpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '20px',
    borderRadius: '8px',
    zIndex: 10,
    fontSize: '14px',
    color: colors['text-secondary'],
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
  },
}

const ThemePosts: React.FC<ThemePostsProps> = ({
  posts: initialPosts,
  theme,
  bargain_user_info,
  random_seed,
}) => {
  const [filteredPosts, setFilteredPosts] = useState<Post[]>(initialPosts)
  const [isLoadingFilteredPosts, setIsLoadingFilteredPosts] = useState(false)
  const [filterByFollowing, setFilterByFollowing] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  const handleFilterByFollowingToggle = () => {
    setFilterByFollowing(!filterByFollowing)
  }

  // フィルタリングが変更された時に新しいデータを取得
  useEffect(() => {
    const fetchFilteredPosts = async () => {
      if (!filterByFollowing) {
        setFilteredPosts(initialPosts)
        return
      }

      const params: PaginationParams = {
        page: 1,
        offset: 0,
        random_seed,
      }

      if (filterByFollowing) {
        params.filter_by_following = true
      }

      setIsLoadingFilteredPosts(true)
      try {
        const response = await fetchThemePosts(theme.id, params)
        setFilteredPosts(response.posts)
      } catch (error) {
        console.error('Failed to fetch filtered posts:', error)
        setFilteredPosts(initialPosts)
      } finally {
        setIsLoadingFilteredPosts(false)
      }
    }

    void fetchFilteredPosts()
  }, [filterByFollowing, theme.id, random_seed, initialPosts])

  const fetchMorePosts = useCallback(
    async (page: number, offset: number) => {
      const params: PaginationParams = {
        page,
        offset,
        random_seed,
      }

      if (filterByFollowing) {
        params.filter_by_following = true
      }

      const response = await fetchThemePosts(theme.id, params)

      return {
        posts: response.posts,
      }
    },
    [theme.id, random_seed, filterByFollowing]
  )

  return (
    <PageContainer maxWidth="640px">
      <PageHeader title="みんなのおいしい口コミ" bargain_user_info={bargain_user_info} />

      <div style={styles.themeInfo}>
        <div style={styles.themeDate}>
          <DateFormatter
            dateString={theme.entry_start_datetime}
            format={'date-only'}
            style={styles.themeDate}
          />
          <span>〜</span>
          <DateFormatter
            dateString={theme.entry_end_datetime}
            format={'date-only'}
            style={styles.themeDate}
          />
          <span>のお題</span>
        </div>
        <h2 style={styles.themeTitle}>「{theme.title}」</h2>
        {theme.description && <p style={styles.themeDescription}>{theme.description}</p>}
      </div>

      {bargain_user_info.following_shops_count > 0 && (
        <div style={styles.filterButtons}>
          <button
            type="button"
            style={{
              ...styles.filterButton,
              ...(filterByFollowing ? styles.filterButtonActive : {}),
              ...(isLoadingFilteredPosts ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
            }}
            onClick={handleFilterByFollowingToggle}
            disabled={isLoadingFilteredPosts}
          >
            フォロー中のチェーン
          </button>
        </div>
      )}

      <div style={styles.caution}>
        ※
        商品名・価格は投稿者の申告情報です。実際と異なる場合がありますので目安としてご覧ください。日付は投稿日時であり、実際の販売期間とは異なる場合があります。
      </div>

      <div style={styles.postList}>
        {isLoadingFilteredPosts ? (
          <div style={styles.filterLoadingOverlay}>
            <div style={styles.filterLoadingSpinner}>
              <div>絞り込み中...</div>
              <div style={{ marginTop: '8px', fontSize: '12px' }}>
                フォロー中のチェーンの投稿を絞り込んでいます
              </div>
            </div>
          </div>
        ) : (
          <InfinitePostList
            initialPosts={filteredPosts}
            bargain_user_info={bargain_user_info}
            fetchMorePosts={fetchMorePosts}
            emptyMessage={
              filterByFollowing
                ? 'フォロー中のチェーンではまだ投稿はありません。'
                : 'まだ投稿はありません。'
            }
          />
        )}
      </div>

      <div style={styles.footerButtons}>
        <BackButton href="/webview/message_board/posts">
          <span className="icon_font icf_arrow_left" style={{ fontSize: '18px' }} />
          すべての口コミ
        </BackButton>

        <BackButton
          href={bargain_user_info.type !== 'guest' ? '/webview/message_board/posts/new' : undefined}
          variant="primary"
          onClick={bargain_user_info.type === 'guest' ? () => setShowLoginModal(true) : undefined}
        >
          <span className="icon_font icf_camera" style={{ fontSize: '18px' }} />
          投稿する
        </BackButton>
      </div>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </PageContainer>
  )
}

export default ThemePosts
